import React, {FC, useEffect, useRef} from 'react'
import {CanvasIcon, Icon, IconName, LayoutChooserList, ViewLinkIcon} from '@dv5/components'
import {DocumentParentView} from '@dv5/api'
import {DocumentPart, DvLayout, PartType} from '@dv5/domain'
import {partTypesTitle} from '../partTypes'
import {getOutlineLabel} from './documentOutlineHelpers'
import './DocumentOutline.less'

const typeIcons: Record<PartType, IconName> = {
    [PartType.MARKDOWN]: IconName.Markdown,
    [PartType.PLAINTEXT]: IconName.Plaintext,
    [PartType.CSV_TABLE]: IconName.CSVTable,
    [PartType.DIARY]: IconName.Diary,
    [PartType.SECRET]: IconName.Lock,
}

interface Props {
    id: string
    parts: DocumentPart[]
    activePartId: string | null
    onSelect: (id: string) => void
    onClose: () => void
    parents: DocumentParentView[]
    parentsLoaded: boolean
    onNavigateToParent: (parent: DocumentParentView) => void
    layouts: DvLayout[]
    selectedLayoutId: string | undefined
    onSelectLayout: (layout: DvLayout) => void
}

export const DocumentOutline: FC<Props> = ({
                                               id,
                                               parts,
                                               activePartId,
                                               onSelect,
                                               onClose,
                                               parents,
                                               parentsLoaded,
                                               onNavigateToParent,
                                               layouts,
                                               selectedLayoutId,
                                               onSelectLayout,
                                           }) => {
    const navRef = useRef<HTMLElement>(null)

    useEffect(() => {
        const nav = navRef.current
        const button = nav?.querySelector<HTMLButtonElement>('[aria-current="location"]')
            || nav?.querySelector<HTMLButtonElement>('button')
        button?.focus({preventScroll: true})
    }, [])

    return (
        <nav id={id} ref={navRef} className="documentOutline" aria-label="Document outline"
             onKeyDown={event => {
                 if (event.key === 'Escape') {
                     event.preventDefault()
                     event.stopPropagation()
                     onClose()
                 }
             }}>
            <div className="documentOutlineHeader">
                <strong>Outline</strong>
                <button type="button" onClick={onClose} aria-label="Close document outline" title="Close outline">×</button>
            </div>
            <div className="documentOutlineParents">
                <div className="documentOutlineParentsTitle">Parent views</div>
                {parentsLoaded && parents.length === 0 && (
                    <div className="documentOutlineParentsEmpty">Document is not placed in any view or canvas</div>
                )}
                {parents.map(parent => (
                    <button
                        key={`${parent.type}_${parent.id}`}
                        className="documentOutlineParentItem"
                        type="button"
                        title={parent.title}
                        aria-label={`Open ${parent.title}`}
                        onClick={() => onNavigateToParent(parent)}
                    >
                        <span className="documentOutlineParentItemIcon">
                            {parent.type === 'CANVAS' ? <CanvasIcon/> : <ViewLinkIcon/>}
                        </span>
                        <span className="documentOutlineParentItemTitle">{parent.title}</span>
                    </button>
                ))}
            </div>
            <div className="documentOutlineList">
                {parts.length === 0 ? <p>No parts to show.</p> : (
                    <ol>
                        {parts.map((part, index) => {
                            const label = getOutlineLabel(part, index)
                            return (
                                <li key={part.id}>
                                    <button type="button" title={`${label} (${partTypesTitle[part.partType]})`}
                                            aria-current={activePartId === part.id ? 'location' : undefined}
                                            onClick={() => onSelect(part.id)}>
                                        <Icon name={typeIcons[part.partType]} width="16" height="16" aria-hidden="true"/>
                                        <span>{label}</span>
                                    </button>
                                </li>
                            )
                        })}
                    </ol>
                )}
            </div>
            {layouts.length > 0 && (
                <div className="documentOutlineFooter">
                    <div className="documentOutlineDivider"/>
                    <LayoutChooserList
                        layouts={layouts}
                        selectedLayoutId={selectedLayoutId}
                        onSelect={onSelectLayout}
                    />
                </div>
            )}
        </nav>
    )
}
